"use client"

import { useEffect, useState } from "react"
import axios from "axios"
import * as XLSX from "xlsx"
import { motion, AnimatePresence } from "framer-motion"
import {
  Building2,
  Mail,
  Phone,
  MapPin,
  Globe,
  Calendar,
  Filter,
  Download,
  Trash2,
  Eye,
  Save,
  X,
  RefreshCw,
  FileText,
  Users,
  Briefcase,
} from "lucide-react"
import MeetingsListModal from "../components/MeetingsListModal";

const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-800 border-blue-200",
  Processing: "bg-yellow-100 text-yellow-800 border-yellow-200",
  Accepted: "bg-green-100 text-green-800 border-green-200",
}

const initialFilters = {
  fullName: "",
  email: "",
  phone: "",
  businessName: "",
  location: "",
  industry: "",
  status: "",
  startDate: "",
  endDate: "",
}

export default function BusinessConsultation() {
  const [businesses, setBusinesses] = useState([])
  const [filter, setFilter] = useState(initialFilters)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const recordsPerPage = 10
  const [loading, setLoading] = useState(false)
  const [showDescriptionPopup, setShowDescriptionPopup] = useState(false)
  const [selectedDescription, setSelectedDescription] = useState("")
  const [editingStatusId, setEditingStatusId] = useState(null)
  const [newStatus, setNewStatus] = useState("")
  const [meetingModalOpen, setMeetingModalOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [meetingsModalOpen, setMeetingsModalOpen] = useState(false);

  const handleStatusChange = async (id, status) => {
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/businessconsultation/${id}`, { status })
      setBusinesses((prevBusinesses) => prevBusinesses.map((item) => (item._id === id ? { ...item, status } : item)))
      setEditingStatusId(null)
    } catch (error) {
      console.error("Failed to update status", error)
    }
  }

  useEffect(() => {
    fetchBusinesses()
    fetchMeetings()
  }, [])

  const fetchBusinesses = async () => {
    setLoading(true)
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/businessesconsulation`)
      setBusinesses(res.data)
    } catch (err) {
      console.error("Fetch error:", err)
      setBusinesses([])
    }
    setLoading(false)
  }

  const fetchMeetings = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/meetings`,
        { params: { meetingType: 'consultation' } }
      );
      const meetingsData = res.data.data || [];

      const consultationOnly = meetingsData.filter(
        (m) => m.meetingType === 'consultation'
      );

      consultationOnly.sort(
        (a, b) => new Date(b.meetingDate) - new Date(a.meetingDate)
      );

      setMeetings(consultationOnly);
    } catch (error) {
      console.error('Error fetching meetings:', error);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/businessesconsultation/${selectedItem}`)
      setDeleteDialogOpen(false)
      setSelectedItem(null)
      fetchBusinesses()
    } catch (err) {
      console.error("Delete error:", err)
    }
  }

  const handleDownloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(businesses)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "Consultation")
    XLSX.writeFile(workbook, "Business_Consultation_List.xlsx")
  }

  const handleClearFilters = () => {
    setFilter(initialFilters)
  }

  const filteredBusinesses = businesses.filter((item) => {
    if (filter.fullName && !item.fullName?.toLowerCase().includes(filter.fullName.toLowerCase())) return false
    if (filter.email && !item.email?.toLowerCase().includes(filter.email.toLowerCase())) return false
    if (filter.phone && !item.phone?.toLowerCase().includes(filter.phone.toLowerCase())) return false
    if (filter.location && item.location?.toLowerCase() !== filter.location.toLowerCase()) return false
    if (filter.businessName && !item.businessName?.toLowerCase().includes(filter.businessName.toLowerCase()))
      return false
    if (filter.industry && !item.industry?.toLowerCase().includes(filter.industry.toLowerCase())) return false
    if (filter.status && item.status !== filter.status) return false
    if (filter.startDate) {
      const itemDate = new Date(item.registeredAt)
      const start = new Date(filter.startDate)
      if (itemDate < start) return false
    }
    if (filter.endDate) {
      const itemDate = new Date(item.registeredAt)
      const end = new Date(filter.endDate)
      if (itemDate > end) return false
    }
    return true
  })

  const totalPages = Math.ceil(filteredBusinesses.length / recordsPerPage)
  const paginated = filteredBusinesses.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage)

  const formatDateTime = (timestamp) => {
    if (!timestamp) return "—"
    const date = new Date(timestamp)
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })}`
  }

  const tamilNaduDistricts = [
    "Ariyalur",
    "Chengalpattu",
    "Chennai",
    "Coimbatore",
    "Cuddalore",
    "Dharmapuri",
    "Dindigul",
    "Erode",
    "Kallakurichi",
    "Kanchipuram",
    "Kanyakumari",
    "Karur",
    "Krishnagiri",
    "Madurai",
    "Mayiladuthurai",
    "Nagapattinam",
    "Namakkal",
    "Nilgiris",
    "Perambalur",
    "Pudukkottai",
    "Ramanathapuram",
    "Ranipet",
    "Salem",
    "Sivaganga",
    "Tenkasi",
    "Thanjavur",
    "Theni",
    "Thoothukudi",
    "Tiruchirappalli",
    "Tirunelveli",
    "Tirupathur",
    "Tiruppur",
    "Tiruvallur",
    "Tiruvannamalai",
    "Tiruvarur",
    "Vellore",
    "Viluppuram",
    "Virudhunagar",
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full mx-auto p-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Business Consultation</h1>
                <p className="text-gray-500">Manage business consultation applications and inquiries</p>
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleDownloadExcel}
              className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export Excel
            </motion.button>
          </div>
        </motion.div>

        {/* Meetings Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Calendar className="text-blue-600 text-lg" />
              <h2 className="text-lg font-semibold text-gray-800">Manage Meetings</h2>
            </div>
            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setMeetingsModalOpen(true)}
                className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-6 py-2 rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
              >
                <Calendar className="w-4 h-4" />
                View Meetings
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <Filter className="text-blue-600 text-lg" />
            <h2 className="text-lg font-semibold text-gray-800">Filters</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Users className="w-3 h-3" />
                Full Name
              </label>
              <input
                type="text"
                value={filter.fullName}
                onChange={(e) => setFilter((prev) => ({ ...prev, fullName: e.target.value }))}
                placeholder="Search by name..."
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 text-sm"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Mail className="w-3 h-3" />
                Email
              </label>
              <input
                type="email"
                value={filter.email}
                onChange={(e) => setFilter((prev) => ({ ...prev, email: e.target.value }))}
                placeholder="Search by email..."
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 text-sm"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Phone className="w-3 h-3" />
                Phone
              </label>
              <input
                type="text"
                value={filter.phone}
                onChange={(e) => setFilter((prev) => ({ ...prev, phone: e.target.value }))}
                placeholder="Search by phone..."
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 text-sm"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Building2 className="w-3 h-3" />
                Business Name
              </label>
              <input
                type="text"
                value={filter.businessName}
                onChange={(e) => setFilter((prev) => ({ ...prev, businessName: e.target.value }))}
                placeholder="Search business..."
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 text-sm"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <MapPin className="w-3 h-3" />
                District
              </label>
              <select
                value={filter.location}
                onChange={(e) => setFilter((prev) => ({ ...prev, location: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 text-sm"
              >
                <option value="">All Districts</option>
                {tamilNaduDistricts.map((district) => (
                  <option key={district} value={district}>
                    {district}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Briefcase className="w-3 h-3" />
                Status
              </label>
              <select
                value={filter.status}
                onChange={(e) => setFilter((prev) => ({ ...prev, status: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 text-sm"
              >
                <option value="">All Status</option>
                <option value="new">New</option>
                <option value="Processing">Processing</option>
                <option value="Accepted">Accepted</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Calendar className="w-3 h-3" />
                Start Date
              </label>
              <input
                type="datetime-local"
                value={filter.startDate}
                onChange={(e) => setFilter((prev) => ({ ...prev, startDate: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 text-sm"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Calendar className="w-3 h-3" />
                End Date
              </label>
              <input
                type="datetime-local"
                value={filter.endDate}
                onChange={(e) => setFilter((prev) => ({ ...prev, endDate: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 text-sm"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleClearFilters}
              className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-6 py-2 rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Clear Filters
            </motion.button>
          </div>
        </motion.div>

        {/* Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Date & Time
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Full Name
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Email
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      Phone
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      District
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Business
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Description
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Website
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <AnimatePresence>
                  {loading ? (
                    <tr>
                      <td colSpan="10" className="px-6 py-12 text-center">
                        <div className="flex items-center justify-center">
                          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                          <span className="ml-3 text-gray-500">Loading consultations...</span>
                        </div>
                      </td>
                    </tr>
                  ) : paginated.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="text-center py-16">
                        <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl flex items-center justify-center mx-auto mb-6">
                          <Users className="text-gray-400 text-3xl" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-700 mb-2">No consultations found</h3>
                        <p className="text-gray-500">Try adjusting your filters to see more results</p>
                      </td>
                    </tr>
                  ) : (
                    paginated.map((item, index) => (
                      <motion.tr
                        key={item._id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ delay: index * 0.05 }}
                        className="hover:bg-blue-50/50 transition-colors duration-200"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {formatDateTime(item.createdAt || item.updatedAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{item.fullName || "—"}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {item.email ? (
                            <a
                              href={`mailto:${item.email}`}
                              className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                              title={item.email}
                            >
                              {item.email.length > 25 ? `${item.email.slice(0, 25)}...` : item.email}
                            </a>
                          ) : (
                            <span className="text-sm text-gray-500">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-600">{item.phone || "—"}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <MapPin className="w-3 h-3" />
                            {item.location || "—"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 max-w-xs truncate" title={item.businessName}>
                            {item.businessName || "—"}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {item.businessDescription ? (
                            <div className="text-sm text-gray-600">
                              {item.businessDescription.length > 30 ? (
                                <>
                                  {item.businessDescription.slice(0, 30)}...
                                  <button
                                    onClick={() => {
                                      setSelectedDescription(item.businessDescription)
                                      setShowDescriptionPopup(true)
                                    }}
                                    className="ml-2 text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                                  >
                                    <Eye className="w-3 h-3" />
                                    Read More
                                  </button>
                                </>
                              ) : (
                                item.businessDescription
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {item.website ? (
                            <a
                              href={item.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                              title={item.website}
                            >
                              <Globe className="w-3 h-3" />
                              Website
                            </a>
                          ) : (
                            <span className="text-sm text-gray-500">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingStatusId === item._id ? (
                            <div className="flex items-center gap-2">
                              <select
                                className="px-3 py-1 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                                value={newStatus}
                                onChange={(e) => setNewStatus(e.target.value)}
                              >
                                {["new", "Processing", "Accepted"].map((status) => (
                                  <option key={status} value={status}>
                                    {status}
                                  </option>
                                ))}
                              </select>
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => handleStatusChange(item._id, newStatus)}
                                disabled={!newStatus}
                                className="p-1 text-green-600 hover:bg-green-100 rounded-full transition-colors duration-200"
                              >
                                <Save className="w-4 h-4" />
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => setEditingStatusId(null)}
                                className="p-1 text-red-600 hover:bg-red-100 rounded-full transition-colors duration-200"
                              >
                                <X className="w-4 h-4" />
                              </motion.button>
                            </div>
                          ) : (
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                setEditingStatusId(item._id)
                                setNewStatus(item.status || "new")
                              }}
                              className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border transition-all duration-200 ${STATUS_COLORS[item.status] || "bg-gray-100 text-gray-800 border-gray-200"
                                }`}
                            >
                              <div
                                className={`w-2 h-2 rounded-full ${item.status === "Accepted"
                                  ? "bg-green-500"
                                  : item.status === "Processing"
                                    ? "bg-yellow-500"
                                    : "bg-blue-500"
                                  }`}
                              ></div>
                              {item.status || "New"}
                            </motion.button>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              setSelectedItem(item._id)
                              setDeleteDialogOpen(true)
                            }}
                            className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:shadow-lg transition-all duration-300 flex items-center gap-1"
                          >
                            <Trash2 className="w-3 h-3" />
                            Cancel
                          </motion.button>
                        </td>
                      </motion.tr>
                    ))
                  )}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Pagination */}
        {totalPages > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex justify-center items-center mt-8 gap-2"
          >
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${currentPage === 1
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white border border-gray-200 text-gray-700 hover:bg-blue-50 hover:border-blue-200"
                }`}
            >
              Previous
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${currentPage === page
                  ? "bg-blue-600 text-white shadow-lg"
                  : "bg-white border border-gray-200 text-gray-700 hover:bg-blue-50 hover:border-blue-200"
                  }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${currentPage === totalPages
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white border border-gray-200 text-gray-700 hover:bg-blue-50 hover:border-blue-200"
                }`}
            >
              Next
            </button>
          </motion.div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteDialogOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Confirm Deletion</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>

              <p className="text-gray-700 mb-6">
                Are you sure you want to delete this consultation record? This will permanently remove it from the
                system.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setDeleteDialogOpen(false)
                    setSelectedItem(null)
                  }}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Description Modal */}
      <AnimatePresence>
        {showDescriptionPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Business Description
                </h3>
                <button
                  onClick={() => setShowDescriptionPopup(false)}
                  className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
                >
                  <X className="w-4 h-4 text-gray-600" />
                </button>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{selectedDescription}</p>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => setShowDescriptionPopup(false)}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <MeetingsListModal
        isOpen={meetingsModalOpen}
        onClose={() => setMeetingsModalOpen(false)}
        meetings={meetings}
        refreshMeetings={fetchMeetings}
      />
    </div>
  )
}